import {
  faCirclePlus,
  faCommentDots,
  faMicroscope,
} from "@fortawesome/sharp-solid-svg-icons"
import { Box } from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { ROUTES } from "../../../utils/constants"
import { extraColors } from "../../../utils/theme"
import type { NewChatMessage } from "../../Chat/useChatSession"
import { useAlertCuration, type BiothreatSignal } from "../AlertCurationContext"
import {
  ActionCell,
  ArchiveCell,
  getSharedAlertCandidateColumns,
} from "./SharedAlertTableColumns"
import { buildBiothreatSignalsContextMessage } from "../../Chat/prompts"

interface GetAlertCandidateColumnsParams {
  isArchivedView: boolean
  triage: (payload: { ids: string[]; action: "archive" | "unarchive" }) => void
}

export const useAlertCandidateColumns = ({
  isArchivedView,
  triage,
}: GetAlertCandidateColumnsParams) => {
  const { pinnedSignals, addToPinnedSignals } = useAlertCuration()
  const actionColumn = {
    field: "action",
    headerName: "",
    width: 110,
    sortable: false,
    align: "left",
    headerAlign: "left",
    renderCell: cellParams => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <ActionCell
          actions={[
            {
              onAction: row => addToPinnedSignals(row),
              icon: faCirclePlus,
              tooltip: "Add to Selected Signals",
              color: extraColors.status.navyLight,
              shouldShow: row => !pinnedSignals.has(row.id),
            },
            {
              icon: faCommentDots,
              tooltip: "Submit Feedback",
              color: extraColors.translucent50,
              iconSize: 18,
            },
            {
              icon: faMicroscope,
              tooltip: "Open in Wingman",
              color: extraColors.status.navyLight,
              iconSize: 18,
              to: (row: BiothreatSignal) => ({
                pathname: ROUTES.WINGMAN,
                search: `?${new URLSearchParams({
                  newChatMessage: JSON.stringify({
                    title: row.title,
                    query: buildBiothreatSignalsContextMessage(row),
                  } satisfies NewChatMessage),
                })}`,
              }),
            },
          ]}
          row={cellParams.row}
        />
      </Box>
    ),
  } satisfies GridColDef<BiothreatSignal>

  const sharedColumns = getSharedAlertCandidateColumns()

  const archiveColumn: GridColDef<BiothreatSignal> = {
    field: "archive",
    headerName: "",
    flex: 0,
    minWidth: 100,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: params => (
      <ArchiveCell
        clusterId={params.row.cluster_id}
        isArchivedView={isArchivedView}
        triage={triage}
      />
    ),
  }

  return [actionColumn, ...sharedColumns, archiveColumn]
}
